import type { AlgorithmResult, SflagaEnhancementOptions } from '../types';
import type { Chromosome, Particle } from '../types/algorithm';
import { runGeneticAlgorithm } from '../algorithms/geneticAlgorithm';
import { runCulturalAlgorithm } from '../algorithms/culturalAlgorithm';
import { runSFLA } from '../algorithms/sfla';
import { runSFLAGABaseline } from '../algorithms/sflaGaBaseline';
import { runSFLAGA } from '../algorithms/sflaga';
import { runPSO } from '../algorithms/pso';
import { runGAPSO } from '../algorithms/gapso';
import {
  ParallelBenchmarkRunner,
  type ParallelBenchmarkAlgorithmRun,
} from './parallelBenchmarkRunner';
import {
  getSharedBenchmarkParameters,
  getRandomizedBenchmarkParameters,
  type SharedBenchmarkParameters,
} from '../utils/parameterUtils';
import {
  RESULT_ALGORITHM_NAMES,
  getResultAlgorithmKeys,
  type ResultAlgorithmKey,
} from '../utils/algorithmRegistry';
import { parseQWSDataset, buildQualityMatrix } from '../utils/qwsParser';
import {
  DEFAULT_ENERGY_AWARE_WEIGHTS,
  FitnessCalculator,
  CostCalculator,
  setGlobalFitnessCalculator,
  clearGlobalFitnessCalculator,
  setGlobalCostCalculator,
  clearGlobalCostCalculator,
  calculateResponseTime,
  calculateEnergy,
  calculateCost,
} from '../utils/fitnessCalculator';
import { generateRunSeed } from '../utils/seededRNG';
import { calculateAlgorithmStatistics, calculateStatistics } from '../utils/statistics';
import {
  createEmptyBenchmarkOperationCounts,
  setActiveBenchmarkOperationCounts,
  type BenchmarkOperationCounts,
} from '../utils/benchmarkInstrumentation';
import { buildWinnerCounts } from '../utils/sflagaImprovementBenchmarkWinnerCounts';
import {
  SFLAGA_IMPROVEMENT_BENCHMARK_SCHEMA_VERSION,
  buildSflagaImprovementBenchmarkConditionSignature,
} from '../utils/sflagaImprovementBenchmarkSchema';

export type BenchmarkMetricKey =
  | 'fitness'
  | 'energy'
  | 'cost'
  | 'responseTime'
  | 'executionTime';

export type BenchmarkSummaryBasis = 'mean' | 'median';
export type BenchmarkOperationKey = keyof BenchmarkOperationCounts;
export type BenchmarkProfileCode = 'g' | 'j';
export type BenchmarkParameterMode = 'shared' | 'randomized';

type Direction = 'higher' | 'lower';
type AlgorithmStatisticsSnapshot = ReturnType<typeof calculateAlgorithmStatistics>;

type ConditionRawResults = Partial<Record<ResultAlgorithmKey, AlgorithmResult[]>>;

export interface BenchmarkMetricSnapshot {
  mean: number;
  median: number;
  stdDev: number;
  min: number;
  max: number;
  q1: number;
  q3: number;
}

export interface BenchmarkAlgorithmSnapshot {
  fitness: BenchmarkMetricSnapshot;
  energy: BenchmarkMetricSnapshot;
  cost: BenchmarkMetricSnapshot;
  responseTime: BenchmarkMetricSnapshot;
  executionTime: BenchmarkMetricSnapshot;
}

export type BenchmarkOperationSnapshot = BenchmarkMetricSnapshot;

export type BenchmarkOperationStatistics = Record<
  BenchmarkOperationKey,
  BenchmarkOperationSnapshot
>;

export interface BenchmarkConvergencePoint {
  generation: number;
  fitness: number;
}

export interface BenchmarkConvergenceSummaryPoint {
  generation: number;
  mean: number;
  median: number;
  min: number;
  max: number;
}

export interface BenchmarkRankingRow {
  algorithmKey: string;
  algorithmName: string;
  value: number;
}

export interface BenchmarkWinnerSummary {
  algorithmKey: string;
  algorithmName: string;
  value: number;
  marginToSecond: number | null;
  secondAlgorithmKey?: string;
  secondAlgorithmName?: string;
  secondValue?: number;
}

export interface BenchmarkWinnerCountRow {
  algorithmKey: string;
  algorithmName: string;
  winCount: number;
}

export interface BenchmarkRunAlgorithmResult {
  algorithmKey: ResultAlgorithmKey;
  algorithmName: string;
  fitness: number;
  energy: number;
  cost: number;
  responseTime: number;
  executionTime: number;
  operations: BenchmarkOperationCounts;
  convergence: BenchmarkConvergencePoint[];
}

export interface BenchmarkRunParameterSummary {
  runNumber: number;
  parameterSeed?: number;
  sharedParameters: SharedBenchmarkParameters;
}

export interface BenchmarkConditionRunSummary {
  runNumber: number;
  runSeed: number;
  parameterSeed?: number;
  sharedParameters: SharedBenchmarkParameters;
  algorithms: Partial<Record<ResultAlgorithmKey, BenchmarkRunAlgorithmResult>>;
}

export interface SflagaImprovementConditionDefinition {
  id: string;
  label: string;
  enabledEnhancementIds: Array<keyof SflagaEnhancementOptions>;
  enhancements: SflagaEnhancementOptions;
}

export interface SflagaImprovementConditionSummary {
  id: string;
  label: string;
  enabledEnhancementIds: string[];
  enhancements: SflagaEnhancementOptions;
  configuration: {
    taskCount: number;
    numberOfRuns: number;
    matrixSeed: number;
    parameterPolicy: string;
    includeSflaGaBaseline: boolean;
    parallelExecution: boolean;
    fitnessMode: string;
    taskSpecificVariationEnabled: boolean;
    serviceCount: number;
  };
  sflaga: BenchmarkAlgorithmSnapshot;
  algorithms: Partial<Record<ResultAlgorithmKey, BenchmarkAlgorithmSnapshot>>;
  operationStatistics: Partial<Record<ResultAlgorithmKey, BenchmarkOperationStatistics>>;
  convergenceByAlgorithm: Partial<
    Record<ResultAlgorithmKey, BenchmarkConvergenceSummaryPoint[]>
  >;
  runs: BenchmarkConditionRunSummary[];
  rankingsByMean: Record<BenchmarkMetricKey, BenchmarkRankingRow[]>;
  rankingsByMedian: Record<BenchmarkMetricKey, BenchmarkRankingRow[]>;
  bestAlgorithmsByMean: Record<BenchmarkMetricKey, BenchmarkWinnerSummary>;
  bestAlgorithmsByMedian: Record<BenchmarkMetricKey, BenchmarkWinnerSummary>;
}

export interface SflagaImprovementBenchmarkWinnerCounts {
  byMetric: Record<BenchmarkMetricKey, BenchmarkWinnerCountRow[]>;
  overall: BenchmarkWinnerCountRow[];
}

export interface SflagaImprovementBenchmarkReport {
  generatedAt: string;
  experiment: {
    benchmarkCode: BenchmarkProfileCode;
    benchmarkSchemaVersion?: number;
    taskCount: number;
    numberOfRuns: number;
    matrixSeed: number;
    includeSflaGaBaseline: boolean;
    parallelExecution: boolean;
    fitnessMode: string;
    taskSpecificVariationEnabled: boolean;
    parameterMode: BenchmarkParameterMode;
    parameterSeed?: number;
    serviceCount: number;
    conditionIds?: string[];
    conditionSignature?: string;
    sharedParameters: SharedBenchmarkParameters;
    runParameters: BenchmarkRunParameterSummary[];
  };
  diagnostics: {
    timingMethod: string;
    resetsCalculatorsPerAlgorithm: boolean;
    measuredScope: string;
    algorithmKeys: ResultAlgorithmKey[];
    algorithmNames: string[];
    notes: string[];
  };
  conditions: SflagaImprovementConditionSummary[];
  crossConditionSflagaRankingByMean: Record<
    BenchmarkMetricKey,
    Array<{ id: string; label: string; mean: number }>
  >;
  crossConditionSflagaRankingByMedian: Record<
    BenchmarkMetricKey,
    Array<{ id: string; label: string; median: number }>
  >;
  winnerCountsByMean: SflagaImprovementBenchmarkWinnerCounts;
  winnerCountsByMedian: SflagaImprovementBenchmarkWinnerCounts;
}

export interface SflagaImprovementBenchmarkProgress {
  phase: 'start' | 'complete' | 'summary';
  completedUnits: number;
  totalUnits: number;
  percent: number;
  conditionIndex: number;
  totalConditions: number;
  conditionId: string;
  conditionLabel: string;
  improvementLabel: string;
  runNumber: number;
  totalRuns: number;
  algorithmKey?: ResultAlgorithmKey;
  algorithmName?: string;
}

export interface SflagaImprovementBenchmarkOptions {
  datasetText: string;
  taskCount: number;
  numberOfRuns?: number;
  matrixSeed?: number;
  includeSflaGaBaseline?: boolean;
  taskSpecificVariationEnabled?: boolean;
  parallelExecution?: boolean;
  benchmarkCode?: BenchmarkProfileCode;
  parameterMode?: BenchmarkParameterMode;
  parameterSeed?: number;
  onProgress?: (progress: SflagaImprovementBenchmarkProgress) => void;
  shouldCancel?: () => boolean;
}

interface MeasuredAlgorithmRun {
  rawResult: AlgorithmResult;
  summary: BenchmarkRunAlgorithmResult;
}

interface ConditionExecutionResult {
  configuration: SflagaImprovementConditionSummary['configuration'];
  statistics: Partial<Record<ResultAlgorithmKey, AlgorithmStatisticsSnapshot>>;
  operationStatistics: Partial<Record<ResultAlgorithmKey, BenchmarkOperationStatistics>>;
  convergenceByAlgorithm: Partial<
    Record<ResultAlgorithmKey, BenchmarkConvergenceSummaryPoint[]>
  >;
  runs: BenchmarkConditionRunSummary[];
}

interface AlgorithmProgressPayload {
  conditionIndex: number;
  totalConditions: number;
  condition: SflagaImprovementConditionDefinition;
  runNumber: number;
  totalRuns: number;
  algorithmKey: ResultAlgorithmKey;
}

function yieldToEventLoop(): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, 0);
  });
}

export const DEFAULT_SFLAGA_IMPROVEMENT_BENCHMARK_NUMBER_OF_RUNS = 5;
export const DEFAULT_SFLAGA_IMPROVEMENT_BENCHMARK_SEED = 20260325;
export const DEFAULT_SFLAGA_IMPROVEMENT_BENCHMARK_INCLUDE_BASELINE = true;
export const DEFAULT_SFLAGA_IMPROVEMENT_BENCHMARK_VARIATION = true;
export const DEFAULT_SFLAGA_IMPROVEMENT_BENCHMARK_FITNESS_MODE = 'energy-aware';

const METRIC_DIRECTIONS: Record<BenchmarkMetricKey, Direction> = {
  fitness: 'higher',
  energy: 'lower',
  cost: 'lower',
  responseTime: 'lower',
  executionTime: 'lower',
};

const ALL_DISABLED_ENHANCEMENTS: SflagaEnhancementOptions = {
  guidedMutation: false,
  elitePreservation: false,
  similarityAwareDiversification: false,
  adaptiveImmigrantRefresh: false,
  worstFrogLocalSearch: true,
};

const ALL_ENABLED_ENHANCEMENTS: SflagaEnhancementOptions = {
  guidedMutation: true,
  elitePreservation: true,
  similarityAwareDiversification: true,
  adaptiveImmigrantRefresh: true,
  worstFrogLocalSearch: true,
};

function createSingleEnhancementSet(
  enabledKey: keyof SflagaEnhancementOptions
): SflagaEnhancementOptions {
  return {
    ...ALL_DISABLED_ENHANCEMENTS,
    [enabledKey]: true,
  };
}

export const SFLAGA_IMPROVEMENT_BENCHMARK_CONDITIONS: SflagaImprovementConditionDefinition[] =
  [
    {
      id: 'allDisabled',
      label: 'All Disabled',
      enabledEnhancementIds: [],
      enhancements: ALL_DISABLED_ENHANCEMENTS,
    },
    {
      id: 'guidedMutation',
      label: 'Guided Mutation Only',
      enabledEnhancementIds: ['guidedMutation'],
      enhancements: createSingleEnhancementSet('guidedMutation'),
    },
    {
      id: 'elitePreservation',
      label: 'Elite Preservation Only',
      enabledEnhancementIds: ['elitePreservation'],
      enhancements: createSingleEnhancementSet('elitePreservation'),
    },
    {
      id: 'similarityAwareDiversification',
      label: 'Similarity Diversification Only',
      enabledEnhancementIds: ['similarityAwareDiversification'],
      enhancements: createSingleEnhancementSet('similarityAwareDiversification'),
    },
    {
      id: 'adaptiveImmigrantRefresh',
      label: 'Adaptive Immigrant Refresh Only',
      enabledEnhancementIds: ['adaptiveImmigrantRefresh'],
      enhancements: createSingleEnhancementSet('adaptiveImmigrantRefresh'),
    },
    {
      id: 'allEnabled',
      label: 'All Enabled',
      enabledEnhancementIds: Object.keys(ALL_ENABLED_ENHANCEMENTS) as Array<
        keyof SflagaEnhancementOptions
      >,
      enhancements: ALL_ENABLED_ENHANCEMENTS,
    },
  ];

function throwIfCancelled(shouldCancel?: () => boolean): void {
  if (shouldCancel?.()) {
    throw new Error('SFLAGA improvement benchmark cancelled');
  }
}

function buildRunParameterSchedule(
  taskCount: number,
  numberOfRuns: number,
  parameterMode: BenchmarkParameterMode,
  parameterSeed?: number
): BenchmarkRunParameterSummary[] {
  const sharedParameters = getSharedBenchmarkParameters(taskCount);

  return Array.from({ length: numberOfRuns }, (_, index) => {
    const runNumber = index + 1;
    if (parameterMode === 'randomized' && parameterSeed) {
      const perRunParameterSeed = generateRunSeed(parameterSeed, runNumber);
      return {
        runNumber,
        parameterSeed: perRunParameterSeed,
        sharedParameters: getRandomizedBenchmarkParameters(
          taskCount,
          perRunParameterSeed
        ),
      };
    }

    return {
      runNumber,
      sharedParameters,
    };
  });
}

function round(value: number, digits: number = 6): number {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
}

function cloneOperationCounts(
  counts: BenchmarkOperationCounts
): BenchmarkOperationCounts {
  return {
    fitnessEvaluations: counts.fitnessEvaluations,
    crossoverCalls: counts.crossoverCalls,
    mutationCalls: counts.mutationCalls,
    localSearchCalls: counts.localSearchCalls,
  };
}

function normalizeMetricSnapshot(
  metric: {
    mean: number;
    median: number;
    stdDev: number;
    min: number;
    max: number;
    q1?: number;
    q3?: number;
  }
): BenchmarkMetricSnapshot {
  return {
    mean: round(metric.mean),
    median: round(metric.median),
    stdDev: round(metric.stdDev),
    min: round(metric.min),
    max: round(metric.max),
    q1: round(metric.q1 ?? metric.median),
    q3: round(metric.q3 ?? metric.median),
  };
}

function emptyAlgorithmSnapshot(): BenchmarkAlgorithmSnapshot {
  const emptyMetric = normalizeMetricSnapshot({
    mean: 0,
    median: 0,
    stdDev: 0,
    min: 0,
    max: 0,
    q1: 0,
    q3: 0,
  });

  return {
    fitness: emptyMetric,
    energy: emptyMetric,
    cost: emptyMetric,
    responseTime: emptyMetric,
    executionTime: emptyMetric,
  };
}

function snapshotStatistics(
  statistics: Partial<Record<ResultAlgorithmKey, AlgorithmStatisticsSnapshot>>
): Partial<Record<ResultAlgorithmKey, BenchmarkAlgorithmSnapshot>> {
  const snapshots: Partial<Record<ResultAlgorithmKey, BenchmarkAlgorithmSnapshot>> = {};

  for (const algorithmKey of Object.keys(statistics) as ResultAlgorithmKey[]) {
    const metrics = statistics[algorithmKey];
    if (!metrics) {
      continue;
    }

    snapshots[algorithmKey] = {
      fitness: normalizeMetricSnapshot(metrics.fitness),
      energy: normalizeMetricSnapshot(metrics.energy),
      cost: normalizeMetricSnapshot(metrics.cost),
      responseTime: normalizeMetricSnapshot(metrics.responseTime),
      executionTime: normalizeMetricSnapshot(metrics.executionTime),
    };
  }

  return snapshots;
}

function sortMetricRows(
  rows: BenchmarkRankingRow[],
  direction: Direction
): BenchmarkRankingRow[] {
  return [...rows].sort((a, b) =>
    direction === 'higher' ? b.value - a.value : a.value - b.value
  );
}

function buildWinnerSummary(
  ranking: BenchmarkRankingRow[],
  direction: Direction
): BenchmarkWinnerSummary {
  const winner = ranking[0];
  const runnerUp = ranking[1];

  return {
    algorithmKey: winner.algorithmKey,
    algorithmName: winner.algorithmName,
    value: round(winner.value),
    marginToSecond: runnerUp
      ? round(
          direction === 'higher'
            ? winner.value - runnerUp.value
            : runnerUp.value - winner.value
        )
      : null,
    secondAlgorithmKey: runnerUp?.algorithmKey,
    secondAlgorithmName: runnerUp?.algorithmName,
    secondValue: runnerUp ? round(runnerUp.value) : undefined,
  };
}

function getMetricValue(
  snapshot: BenchmarkAlgorithmSnapshot,
  metricKey: BenchmarkMetricKey,
  basis: BenchmarkSummaryBasis
): number {
  return snapshot[metricKey][basis];
}

function toSummaryAlgorithmResult(
  algorithmKey: ResultAlgorithmKey,
  rawResult: AlgorithmResult,
  operations: BenchmarkOperationCounts,
  convergence: BenchmarkConvergencePoint[]
): BenchmarkRunAlgorithmResult {
  return {
    algorithmKey,
    algorithmName: RESULT_ALGORITHM_NAMES[algorithmKey],
    fitness: round(rawResult.fitness),
    energy: round(rawResult.energy),
    cost: round(rawResult.cost),
    responseTime: round(rawResult.responseTime),
    executionTime: round(rawResult.executionTime ?? 0),
    operations: cloneOperationCounts(operations),
    convergence: convergence.map((point) => ({
      generation: point.generation,
      fitness: round(point.fitness),
    })),
  };
}

function convertChromosomeToResult(
  chromosome: Chromosome,
  executionTime: number,
  matrix: number[][][],
  taskCount: number,
  serviceCount: number
): AlgorithmResult {
  return {
    fitness: chromosome.fitness,
    solution: chromosome.x,
    energy: calculateEnergy(chromosome.x, matrix, taskCount, serviceCount),
    cost: calculateCost(chromosome.x, matrix, taskCount, serviceCount),
    responseTime: calculateResponseTime(chromosome.x, matrix, taskCount, serviceCount),
    executionTime,
  };
}

function convertParticleToResult(
  particle: Particle,
  executionTime: number,
  matrix: number[][][],
  taskCount: number,
  serviceCount: number
): AlgorithmResult {
  return {
    fitness: particle.fitness,
    solution: particle.position,
    energy: calculateEnergy(particle.position, matrix, taskCount, serviceCount),
    cost: calculateCost(particle.position, matrix, taskCount, serviceCount),
    responseTime: calculateResponseTime(
      particle.position,
      matrix,
      taskCount,
      serviceCount
    ),
    executionTime,
  };
}

function initializeCalculators(
  matrix: number[][][],
  taskCount: number,
  serviceCount: number,
  seed: number
): { fitnessCalculator: FitnessCalculator } {
  clearGlobalFitnessCalculator();
  clearGlobalCostCalculator();

  const costCalculator = new CostCalculator();
  costCalculator.initialize(matrix, taskCount, serviceCount);
  setGlobalCostCalculator(costCalculator);

  const fitnessCalculator = new FitnessCalculator();
  fitnessCalculator.initialize(
    matrix,
    taskCount,
    serviceCount,
    DEFAULT_SFLAGA_IMPROVEMENT_BENCHMARK_FITNESS_MODE,
    DEFAULT_ENERGY_AWARE_WEIGHTS,
    costCalculator,
    seed
  );
  setGlobalFitnessCalculator(fitnessCalculator);

  return { fitnessCalculator };
}

function clearCalculators(fitnessCalculator?: FitnessCalculator): void {
  if (fitnessCalculator) {
    fitnessCalculator.clearCache();
  }
  clearGlobalFitnessCalculator();
  clearGlobalCostCalculator();
}

async function measureChromosomeAlgorithm(
  algorithmKey: ResultAlgorithmKey,
  matrix: number[][][],
  taskCount: number,
  serviceCount: number,
  matrixSeed: number,
  execute: (
    onGenerationComplete: (generation: number, bestFitness: number) => void
  ) => Promise<Chromosome>
): Promise<MeasuredAlgorithmRun> {
  const { fitnessCalculator } = initializeCalculators(
    matrix,
    taskCount,
    serviceCount,
    matrixSeed
  );
  const operations = createEmptyBenchmarkOperationCounts();
  const convergence: BenchmarkConvergencePoint[] = [];

  setActiveBenchmarkOperationCounts(operations);

  try {
    const startedAt = performance.now();
    const chromosome = await execute((generation, bestFitness) => {
      convergence.push({ generation, fitness: bestFitness });
    });
    const rawResult = convertChromosomeToResult(
      chromosome,
      performance.now() - startedAt,
      matrix,
      taskCount,
      serviceCount
    );

    return {
      rawResult,
      summary: toSummaryAlgorithmResult(
        algorithmKey,
        rawResult,
        operations,
        convergence
      ),
    };
  } finally {
    setActiveBenchmarkOperationCounts(null);
    clearCalculators(fitnessCalculator);
  }
}

async function measureParticleAlgorithm(
  algorithmKey: ResultAlgorithmKey,
  matrix: number[][][],
  taskCount: number,
  serviceCount: number,
  matrixSeed: number,
  execute: (
    onGenerationComplete: (generation: number, bestFitness: number) => void
  ) => Promise<Particle>
): Promise<MeasuredAlgorithmRun> {
  const { fitnessCalculator } = initializeCalculators(
    matrix,
    taskCount,
    serviceCount,
    matrixSeed
  );
  const operations = createEmptyBenchmarkOperationCounts();
  const convergence: BenchmarkConvergencePoint[] = [];

  setActiveBenchmarkOperationCounts(operations);

  try {
    const startedAt = performance.now();
    const particle = await execute((generation, bestFitness) => {
      convergence.push({ generation, fitness: bestFitness });
    });
    const rawResult = convertParticleToResult(
      particle,
      performance.now() - startedAt,
      matrix,
      taskCount,
      serviceCount
    );

    return {
      rawResult,
      summary: toSummaryAlgorithmResult(
        algorithmKey,
        rawResult,
        operations,
        convergence
      ),
    };
  } finally {
    setActiveBenchmarkOperationCounts(null);
    clearCalculators(fitnessCalculator);
  }
}

function buildOperationStatistics(
  runs: BenchmarkConditionRunSummary[],
  algorithmKey: ResultAlgorithmKey
): BenchmarkOperationStatistics {
  const valuesByKey = {
    fitnessEvaluations: [] as number[],
    crossoverCalls: [] as number[],
    mutationCalls: [] as number[],
    localSearchCalls: [] as number[],
  };

  runs.forEach((run) => {
    const result = run.algorithms[algorithmKey];
    if (!result) {
      return;
    }

    valuesByKey.fitnessEvaluations.push(result.operations.fitnessEvaluations);
    valuesByKey.crossoverCalls.push(result.operations.crossoverCalls);
    valuesByKey.mutationCalls.push(result.operations.mutationCalls);
    valuesByKey.localSearchCalls.push(result.operations.localSearchCalls);
  });

  return {
    fitnessEvaluations: normalizeMetricSnapshot(
      calculateStatistics(valuesByKey.fitnessEvaluations)
    ),
    crossoverCalls: normalizeMetricSnapshot(
      calculateStatistics(valuesByKey.crossoverCalls)
    ),
    mutationCalls: normalizeMetricSnapshot(
      calculateStatistics(valuesByKey.mutationCalls)
    ),
    localSearchCalls: normalizeMetricSnapshot(
      calculateStatistics(valuesByKey.localSearchCalls)
    ),
  };
}

function buildConvergenceSummary(
  runs: BenchmarkConditionRunSummary[],
  algorithmKey: ResultAlgorithmKey
): BenchmarkConvergenceSummaryPoint[] {
  const valuesByGeneration = new Map<number, number[]>();

  runs.forEach((run) => {
    const convergence = run.algorithms[algorithmKey]?.convergence ?? [];
    convergence.forEach((point) => {
      const values = valuesByGeneration.get(point.generation) ?? [];
      values.push(point.fitness);
      valuesByGeneration.set(point.generation, values);
    });
  });

  return Array.from(valuesByGeneration.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([generation, values]) => {
      const stats = calculateStatistics(values);
      return {
        generation,
        mean: round(stats.mean),
        median: round(stats.median),
        min: round(stats.min),
        max: round(stats.max),
      };
    });
}

async function executeConditionRuns(
  matrix: number[][][],
  serviceCount: number,
  runParameterSchedule: BenchmarkRunParameterSummary[],
  condition: SflagaImprovementConditionDefinition,
  options: Required<
    Pick<
      SflagaImprovementBenchmarkOptions,
      | 'taskCount'
      | 'numberOfRuns'
      | 'matrixSeed'
      | 'includeSflaGaBaseline'
      | 'taskSpecificVariationEnabled'
      | 'parallelExecution'
    >
  >,
  conditionIndex: number,
  totalConditions: number,
  shouldCancel?: () => boolean,
  onAlgorithmStart?: (payload: AlgorithmProgressPayload) => void,
  onAlgorithmComplete?: (payload: AlgorithmProgressPayload) => void
): Promise<ConditionExecutionResult> {
  const algorithmKeys = getResultAlgorithmKeys(options.includeSflaGaBaseline);
  const rawResults: ConditionRawResults = {};
  const runs: BenchmarkConditionRunSummary[] = [];
  const parallelRunner =
    options.parallelExecution && typeof Worker !== 'undefined'
      ? new ParallelBenchmarkRunner()
      : null;

  algorithmKeys.forEach((algorithmKey) => {
    rawResults[algorithmKey] = [];
  });

  try {
    for (let runIndex = 0; runIndex < options.numberOfRuns; runIndex++) {
      throwIfCancelled(shouldCancel);

      const runNumber = runIndex + 1;
      const runSeed = generateRunSeed(options.matrixSeed, runNumber);
      const runParameterSummary =
        runParameterSchedule[runIndex] ?? {
          runNumber,
          sharedParameters: getSharedBenchmarkParameters(options.taskCount),
        };
      const sharedParameters = runParameterSummary.sharedParameters;
      const runSummary: BenchmarkConditionRunSummary = {
        runNumber,
        runSeed,
        parameterSeed: runParameterSummary.parameterSeed,
        sharedParameters,
        algorithms: {},
      };

      const buildProgressPayload = (
        algorithmKey: ResultAlgorithmKey
      ): AlgorithmProgressPayload => ({
        conditionIndex,
        totalConditions,
        condition,
        runNumber,
        totalRuns: options.numberOfRuns,
        algorithmKey,
      });

      const storeMeasuredResult = (
        algorithmKey: ResultAlgorithmKey,
        measured: MeasuredAlgorithmRun
      ) => {
        rawResults[algorithmKey]?.push(measured.rawResult);
        runSummary.algorithms[algorithmKey] = measured.summary;
      };

      const storeParallelResult = (
        algorithmKey: ResultAlgorithmKey,
        measured: ParallelBenchmarkAlgorithmRun
      ) => {
        rawResults[algorithmKey]?.push(measured.result);
        runSummary.algorithms[algorithmKey] = toSummaryAlgorithmResult(
          algorithmKey,
          measured.result,
          measured.operations,
          measured.convergence
        );
      };

      const executeAndStore = async (
        algorithmKey: ResultAlgorithmKey,
        runner: () => Promise<MeasuredAlgorithmRun>
      ): Promise<void> => {
        throwIfCancelled(shouldCancel);

        const progressPayload = buildProgressPayload(algorithmKey);
        onAlgorithmStart?.(progressPayload);
        await yieldToEventLoop();

        const measured = await runner();
        storeMeasuredResult(algorithmKey, measured);
        onAlgorithmComplete?.(progressPayload);
        await yieldToEventLoop();
      };

      if (parallelRunner) {
        await parallelRunner.run({
          matrix,
          taskCount: options.taskCount,
          serviceCount,
          includeSflaGaBaseline: options.includeSflaGaBaseline,
          sharedParameters,
          runSeed,
          matrixSeed: options.matrixSeed,
          sflagaEnhancements: condition.enhancements,
          onAlgorithmStart: (algorithmKey) => {
            onAlgorithmStart?.(buildProgressPayload(algorithmKey));
          },
          onAlgorithmComplete: (algorithmKey, measured) => {
            storeParallelResult(algorithmKey, measured);
            onAlgorithmComplete?.(buildProgressPayload(algorithmKey));
          },
          shouldCancel,
        });
      } else {
        await executeAndStore('ga', () =>
          measureChromosomeAlgorithm(
            'ga',
            matrix,
            options.taskCount,
            serviceCount,
            options.matrixSeed,
            (onGenerationComplete) =>
              runGeneticAlgorithm(
                options.taskCount,
                serviceCount,
                matrix,
                {
                  populationSize: sharedParameters.initialPopulation,
                  generations: sharedParameters.iterations,
                  selectionRate: sharedParameters.selectionRate,
                  crossoverRate: sharedParameters.crossoverRate,
                  mutationRate: sharedParameters.mutationRate,
                },
                onGenerationComplete,
                runSeed,
                {
                  yieldForProgress: false,
                  reportEveryGeneration: true,
                }
              )
          )
        );

        await executeAndStore('culture', () =>
          measureChromosomeAlgorithm(
            'culture',
            matrix,
            options.taskCount,
            serviceCount,
            options.matrixSeed,
            (onGenerationComplete) =>
              runCulturalAlgorithm(
                options.taskCount,
                serviceCount,
                matrix,
                {
                  populationSize: sharedParameters.initialPopulation,
                  generations: sharedParameters.iterations,
                  selectionRate: sharedParameters.selectionRate,
                  crossoverRate: sharedParameters.crossoverRate,
                  mutationRate: sharedParameters.mutationRate,
                },
                onGenerationComplete,
                runSeed,
                {
                  yieldForProgress: false,
                  reportEveryGeneration: true,
                }
              )
          )
        );

        await executeAndStore('frog', () =>
          measureChromosomeAlgorithm(
            'frog',
            matrix,
            options.taskCount,
            serviceCount,
            options.matrixSeed,
            (onGenerationComplete) =>
              runSFLA(
                options.taskCount,
                serviceCount,
                matrix,
                {
                  populationSize: sharedParameters.initialPopulation,
                  generations: sharedParameters.iterations,
                  memeplexCount: sharedParameters.memeplexCount,
                },
                onGenerationComplete,
                runSeed,
                {
                  yieldForProgress: false,
                  reportEveryGeneration: true,
                }
              )
          )
        );

        if (options.includeSflaGaBaseline) {
          await executeAndStore('sflaGa', () =>
            measureChromosomeAlgorithm(
              'sflaGa',
              matrix,
              options.taskCount,
              serviceCount,
              options.matrixSeed,
              (onGenerationComplete) =>
                runSFLAGABaseline(
                  options.taskCount,
                  serviceCount,
                  matrix,
                  {
                    populationSize: sharedParameters.initialPopulation,
                    generations: sharedParameters.iterations,
                    selectionRate: sharedParameters.selectionRate,
                    memeplexCount: sharedParameters.memeplexCount,
                    crossoverRate: sharedParameters.crossoverRate,
                    mutationRate: sharedParameters.mutationRate,
                  },
                  onGenerationComplete,
                  runSeed,
                  {
                    yieldForProgress: false,
                    reportEveryGeneration: true,
                  }
                )
            )
          );
        }

        await executeAndStore('sflaga', () =>
          measureChromosomeAlgorithm(
            'sflaga',
            matrix,
            options.taskCount,
            serviceCount,
            options.matrixSeed,
            (onGenerationComplete) =>
              runSFLAGA(
                options.taskCount,
                serviceCount,
                matrix,
                {
                  populationSize: sharedParameters.initialPopulation,
                  generations: sharedParameters.iterations,
                  selectionRate: sharedParameters.selectionRate,
                  memeplexCount: sharedParameters.memeplexCount,
                  crossoverRate: sharedParameters.crossoverRate,
                  mutationRate: sharedParameters.mutationRate,
                  sflagaEnhancements: condition.enhancements,
                },
                onGenerationComplete,
                runSeed,
                {
                  yieldForProgress: false,
                  reportEveryGeneration: true,
                }
              )
          )
        );

        await executeAndStore('pso', () =>
          measureParticleAlgorithm(
            'pso',
            matrix,
            options.taskCount,
            serviceCount,
            options.matrixSeed,
            (onGenerationComplete) =>
              runPSO(
                options.taskCount,
                serviceCount,
                matrix,
                {
                  populationSize: sharedParameters.initialPopulation,
                  generations: sharedParameters.iterations,
                },
                onGenerationComplete,
                runSeed,
                {
                  yieldForProgress: false,
                  reportEveryGeneration: true,
                }
              )
          )
        );

        await executeAndStore('gapso', () =>
          measureParticleAlgorithm(
            'gapso',
            matrix,
            options.taskCount,
            serviceCount,
            options.matrixSeed,
            (onGenerationComplete) =>
              runGAPSO(
                options.taskCount,
                serviceCount,
                matrix,
                {
                  populationSize: sharedParameters.initialPopulation,
                  generations: sharedParameters.iterations,
                  selectionRate: sharedParameters.selectionRate,
                  crossoverRate: sharedParameters.crossoverRate,
                  mutationRate: sharedParameters.mutationRate,
                },
                onGenerationComplete,
                runSeed,
                {
                  yieldForProgress: false,
                  reportEveryGeneration: true,
                }
              )
          )
        );
      }

      runs.push(runSummary);
    }
  } finally {
    parallelRunner?.cancel();
  }

  const statistics: Partial<Record<ResultAlgorithmKey, AlgorithmStatisticsSnapshot>> = {};
  const operationStatistics: Partial<Record<ResultAlgorithmKey, BenchmarkOperationStatistics>> =
    {};
  const convergenceByAlgorithm: Partial<
    Record<ResultAlgorithmKey, BenchmarkConvergenceSummaryPoint[]>
  > = {};

  algorithmKeys.forEach((algorithmKey) => {
    const results = rawResults[algorithmKey] ?? [];
    statistics[algorithmKey] = calculateAlgorithmStatistics(results);
    operationStatistics[algorithmKey] = buildOperationStatistics(runs, algorithmKey);
    convergenceByAlgorithm[algorithmKey] = buildConvergenceSummary(runs, algorithmKey);
  });

  const parameterPolicy =
    runParameterSchedule[0]?.sharedParameters.parameterPolicy ??
    getSharedBenchmarkParameters(options.taskCount).parameterPolicy;

  return {
    configuration: {
      taskCount: options.taskCount,
      numberOfRuns: options.numberOfRuns,
      matrixSeed: options.matrixSeed,
      parameterPolicy,
      includeSflaGaBaseline: options.includeSflaGaBaseline,
      parallelExecution: options.parallelExecution,
      fitnessMode: DEFAULT_SFLAGA_IMPROVEMENT_BENCHMARK_FITNESS_MODE,
      taskSpecificVariationEnabled: options.taskSpecificVariationEnabled,
      serviceCount,
    },
    statistics,
    operationStatistics,
    convergenceByAlgorithm,
    runs,
  };
}

function buildConditionSummary(
  condition: SflagaImprovementConditionDefinition,
  result: ConditionExecutionResult
): SflagaImprovementConditionSummary {
  const algorithmStats = snapshotStatistics(result.statistics);
  const algorithmKeys = getResultAlgorithmKeys(result.configuration.includeSflaGaBaseline);

  const rankingsByMean = {} as Record<BenchmarkMetricKey, BenchmarkRankingRow[]>;
  const rankingsByMedian = {} as Record<BenchmarkMetricKey, BenchmarkRankingRow[]>;
  const bestAlgorithmsByMean = {} as Record<
    BenchmarkMetricKey,
    BenchmarkWinnerSummary
  >;
  const bestAlgorithmsByMedian = {} as Record<
    BenchmarkMetricKey,
    BenchmarkWinnerSummary
  >;

  (Object.keys(METRIC_DIRECTIONS) as BenchmarkMetricKey[]).forEach((metricKey) => {
    const rows = algorithmKeys
      .filter((algorithmKey) => Boolean(algorithmStats[algorithmKey]))
      .map((algorithmKey) => ({
        algorithmKey,
        algorithmName: RESULT_ALGORITHM_NAMES[algorithmKey],
        meanValue: getMetricValue(
          algorithmStats[algorithmKey] ?? emptyAlgorithmSnapshot(),
          metricKey,
          'mean'
        ),
        medianValue: getMetricValue(
          algorithmStats[algorithmKey] ?? emptyAlgorithmSnapshot(),
          metricKey,
          'median'
        ),
      }));

    const meanRanking = sortMetricRows(
      rows.map((row) => ({
        algorithmKey: row.algorithmKey,
        algorithmName: row.algorithmName,
        value: round(row.meanValue),
      })),
      METRIC_DIRECTIONS[metricKey]
    );

    const medianRanking = sortMetricRows(
      rows.map((row) => ({
        algorithmKey: row.algorithmKey,
        algorithmName: row.algorithmName,
        value: round(row.medianValue),
      })),
      METRIC_DIRECTIONS[metricKey]
    );

    rankingsByMean[metricKey] = meanRanking;
    rankingsByMedian[metricKey] = medianRanking;
    bestAlgorithmsByMean[metricKey] = buildWinnerSummary(
      meanRanking,
      METRIC_DIRECTIONS[metricKey]
    );
    bestAlgorithmsByMedian[metricKey] = buildWinnerSummary(
      medianRanking,
      METRIC_DIRECTIONS[metricKey]
    );
  });

  return {
    id: condition.id,
    label: condition.label,
    enabledEnhancementIds: [...condition.enabledEnhancementIds],
    enhancements: condition.enhancements,
    configuration: result.configuration,
    sflaga: algorithmStats.sflaga ?? emptyAlgorithmSnapshot(),
    algorithms: algorithmStats,
    operationStatistics: result.operationStatistics,
    convergenceByAlgorithm: result.convergenceByAlgorithm,
    runs: result.runs,
    rankingsByMean,
    rankingsByMedian,
    bestAlgorithmsByMean,
    bestAlgorithmsByMedian,
  };
}

function buildCrossConditionRankingByMean(
  conditions: SflagaImprovementConditionSummary[]
): SflagaImprovementBenchmarkReport['crossConditionSflagaRankingByMean'] {
  const ranking = {} as SflagaImprovementBenchmarkReport['crossConditionSflagaRankingByMean'];

  (Object.keys(METRIC_DIRECTIONS) as BenchmarkMetricKey[]).forEach((metricKey) => {
    const rows = conditions.map((condition) => ({
      id: condition.id,
      label: condition.label,
      mean: round(condition.sflaga[metricKey].mean),
    }));

    ranking[metricKey] = [...rows].sort((a, b) =>
      METRIC_DIRECTIONS[metricKey] === 'higher' ? b.mean - a.mean : a.mean - b.mean
    );
  });

  return ranking;
}

function buildCrossConditionRankingByMedian(
  conditions: SflagaImprovementConditionSummary[]
): SflagaImprovementBenchmarkReport['crossConditionSflagaRankingByMedian'] {
  const ranking = {} as SflagaImprovementBenchmarkReport['crossConditionSflagaRankingByMedian'];

  (Object.keys(METRIC_DIRECTIONS) as BenchmarkMetricKey[]).forEach((metricKey) => {
    const rows = conditions.map((condition) => ({
      id: condition.id,
      label: condition.label,
      median: round(condition.sflaga[metricKey].median),
    }));

    ranking[metricKey] = [...rows].sort((a, b) =>
      METRIC_DIRECTIONS[metricKey] === 'higher'
        ? b.median - a.median
        : a.median - b.median
    );
  });

  return ranking;
}

function createProgressHandlers(args: {
  input: SflagaImprovementBenchmarkOptions;
  totalUnits: number;
  totalConditions: number;
  currentConditionIndex: number;
  progressState: { completedUnits: number };
}) {
  return {
    onAlgorithmStart: ({
      runNumber,
      totalRuns,
      condition,
      algorithmKey,
    }: AlgorithmProgressPayload) => {
      const completedUnits = args.progressState.completedUnits;
      args.input.onProgress?.({
        phase: 'start',
        completedUnits,
        totalUnits: args.totalUnits,
        percent:
          args.totalUnits === 0 ? 0 : (completedUnits / args.totalUnits) * 100,
        conditionIndex: args.currentConditionIndex + 1,
        totalConditions: args.totalConditions,
        conditionId: condition.id,
        conditionLabel: condition.label,
        improvementLabel: condition.label,
        runNumber,
        totalRuns,
        algorithmKey,
        algorithmName: RESULT_ALGORITHM_NAMES[algorithmKey],
      });
    },
    onAlgorithmComplete: ({
      runNumber,
      totalRuns,
      condition,
      algorithmKey,
    }: AlgorithmProgressPayload) => {
      args.progressState.completedUnits += 1;
      const completedUnits = args.progressState.completedUnits;
      args.input.onProgress?.({
        phase: 'complete',
        completedUnits,
        totalUnits: args.totalUnits,
        percent:
          args.totalUnits === 0 ? 100 : (completedUnits / args.totalUnits) * 100,
        conditionIndex: args.currentConditionIndex + 1,
        totalConditions: args.totalConditions,
        conditionId: condition.id,
        conditionLabel: condition.label,
        improvementLabel: condition.label,
        runNumber,
        totalRuns,
        algorithmKey,
        algorithmName: RESULT_ALGORITHM_NAMES[algorithmKey],
      });
    },
  };
}

function toAlgorithmResult(
  summary: BenchmarkRunAlgorithmResult
): AlgorithmResult {
  return {
    fitness: summary.fitness,
    solution: [],
    energy: summary.energy,
    cost: summary.cost,
    responseTime: summary.responseTime,
    executionTime: summary.executionTime,
  };
}

export function filterSflagaImprovementBenchmarkReport(
  report: SflagaImprovementBenchmarkReport,
  selectedRunNumbers: number[]
): SflagaImprovementBenchmarkReport {
  const runNumberSet = new Set(
    selectedRunNumbers
      .map((value) => Math.trunc(value))
      .filter((value) => Number.isFinite(value) && value > 0)
  );

  if (
    runNumberSet.size === 0 ||
    runNumberSet.size >= report.experiment.numberOfRuns
  ) {
    return report;
  }

  const filteredRunParameters = (
    report.experiment.runParameters ?? []
  ).filter((run) => runNumberSet.has(run.runNumber));
  const fallbackRunParameters = report.conditions[0]?.runs
    .filter((run) => runNumberSet.has(run.runNumber))
    .map((run) => ({
      runNumber: run.runNumber,
      parameterSeed: run.parameterSeed,
      sharedParameters: run.sharedParameters ?? report.experiment.sharedParameters,
    }));

  const conditions = report.conditions.map((condition) => {
    const runs = condition.runs.filter((run) => runNumberSet.has(run.runNumber));
    const statistics: Partial<Record<ResultAlgorithmKey, AlgorithmStatisticsSnapshot>> = {};
    const operationStatistics: Partial<
      Record<ResultAlgorithmKey, BenchmarkOperationStatistics>
    > = {};
    const convergenceByAlgorithm: Partial<
      Record<ResultAlgorithmKey, BenchmarkConvergenceSummaryPoint[]>
    > = {};
    const algorithmKeys = getResultAlgorithmKeys(
      condition.configuration.includeSflaGaBaseline
    );

    algorithmKeys.forEach((algorithmKey) => {
      const results = runs
        .map((run) => run.algorithms[algorithmKey])
        .filter(
          (algorithm): algorithm is BenchmarkRunAlgorithmResult => Boolean(algorithm)
        )
        .map(toAlgorithmResult);

      statistics[algorithmKey] = calculateAlgorithmStatistics(results);
      operationStatistics[algorithmKey] = buildOperationStatistics(runs, algorithmKey);
      convergenceByAlgorithm[algorithmKey] = buildConvergenceSummary(
        runs,
        algorithmKey
      );
    });

    return buildConditionSummary(
      {
        id: condition.id,
        label: condition.label,
        enabledEnhancementIds:
          condition.enabledEnhancementIds as Array<keyof SflagaEnhancementOptions>,
        enhancements: condition.enhancements,
      },
      {
        configuration: {
          ...condition.configuration,
          numberOfRuns: runs.length,
          parameterPolicy:
            runs[0]?.sharedParameters?.parameterPolicy ??
            condition.configuration.parameterPolicy,
        },
        statistics,
        operationStatistics,
        convergenceByAlgorithm,
        runs,
      }
    );
  });

  const sharedParameters =
    filteredRunParameters[0]?.sharedParameters ?? report.experiment.sharedParameters;
  const runParameters =
    filteredRunParameters.length > 0
      ? filteredRunParameters
      : fallbackRunParameters;
  const filteredRunCount =
    runParameters?.length ?? conditions[0]?.runs.length ?? report.experiment.numberOfRuns;
  const conditionIds = conditions.map((condition) => condition.id);

  return {
    ...report,
    experiment: {
      ...report.experiment,
      benchmarkSchemaVersion:
        report.experiment.benchmarkSchemaVersion ??
        SFLAGA_IMPROVEMENT_BENCHMARK_SCHEMA_VERSION,
      numberOfRuns: filteredRunCount,
      sharedParameters,
      runParameters,
      conditionIds,
      conditionSignature:
        report.experiment.conditionSignature ??
        buildSflagaImprovementBenchmarkConditionSignature(conditionIds),
    },
    conditions,
    crossConditionSflagaRankingByMean: buildCrossConditionRankingByMean(conditions),
    crossConditionSflagaRankingByMedian: buildCrossConditionRankingByMedian(
      conditions
    ),
    winnerCountsByMean: buildWinnerCounts(
      conditions,
      'mean',
      Object.keys(METRIC_DIRECTIONS) as BenchmarkMetricKey[]
    ),
    winnerCountsByMedian: buildWinnerCounts(
      conditions,
      'median',
      Object.keys(METRIC_DIRECTIONS) as BenchmarkMetricKey[]
    ),
  };
}

export async function runSflagaImprovementBenchmark(
  input: SflagaImprovementBenchmarkOptions
): Promise<SflagaImprovementBenchmarkReport> {
  if (!input.datasetText.trim()) {
    throw new Error('Dataset text is required');
  }

  if (!Number.isFinite(input.taskCount) || input.taskCount < 1) {
    throw new Error('Task count must be at least 1');
  }

  const options = {
    taskCount: Math.max(1, Math.trunc(input.taskCount)),
    numberOfRuns:
      input.numberOfRuns ?? DEFAULT_SFLAGA_IMPROVEMENT_BENCHMARK_NUMBER_OF_RUNS,
    matrixSeed: input.matrixSeed ?? DEFAULT_SFLAGA_IMPROVEMENT_BENCHMARK_SEED,
    includeSflaGaBaseline:
      input.includeSflaGaBaseline ??
      DEFAULT_SFLAGA_IMPROVEMENT_BENCHMARK_INCLUDE_BASELINE,
    taskSpecificVariationEnabled:
      input.taskSpecificVariationEnabled ??
      DEFAULT_SFLAGA_IMPROVEMENT_BENCHMARK_VARIATION,
  };
  const benchmarkCode: BenchmarkProfileCode = input.benchmarkCode ?? 'g';
  const parameterMode: BenchmarkParameterMode =
    input.parameterMode ?? (benchmarkCode === 'j' ? 'randomized' : 'shared');
  const requestedParallelExecution = Boolean(input.parallelExecution);
  const parallelExecution =
    requestedParallelExecution && typeof Worker !== 'undefined';
  const parameterSeed =
    parameterMode === 'randomized'
      ? Math.max(
          1,
          Math.trunc(input.parameterSeed ?? Math.floor(Date.now() % 2147483647))
        )
      : undefined;

  if (!Number.isFinite(options.numberOfRuns) || options.numberOfRuns < 1) {
    throw new Error('Number of runs must be at least 1');
  }

  throwIfCancelled(input.shouldCancel);

  const services = parseQWSDataset(input.datasetText);
  if (services.length === 0) {
    throw new Error('Dataset could not be parsed or contains no services');
  }

  const { matrix, seed } = buildQualityMatrix(
    services,
    options.taskCount,
    options.matrixSeed,
    options.taskSpecificVariationEnabled
  );
  const runParameterSchedule = buildRunParameterSchedule(
    options.taskCount,
    options.numberOfRuns,
    parameterMode,
    parameterSeed
  );
  const sharedParameters =
    runParameterSchedule[0]?.sharedParameters ??
    getSharedBenchmarkParameters(options.taskCount);
  const algorithmKeys = getResultAlgorithmKeys(options.includeSflaGaBaseline);
  const totalUnits =
    SFLAGA_IMPROVEMENT_BENCHMARK_CONDITIONS.length *
    options.numberOfRuns *
    algorithmKeys.length;
  const progressState = { completedUnits: 0 };
  const conditions: SflagaImprovementConditionSummary[] = [];

  for (
    let conditionIndex = 0;
    conditionIndex < SFLAGA_IMPROVEMENT_BENCHMARK_CONDITIONS.length;
    conditionIndex += 1
  ) {
    throwIfCancelled(input.shouldCancel);

    const currentConditionIndex = conditionIndex;
    const condition = SFLAGA_IMPROVEMENT_BENCHMARK_CONDITIONS[conditionIndex];
    const progressHandlers = createProgressHandlers({
      input,
      totalUnits,
      totalConditions: SFLAGA_IMPROVEMENT_BENCHMARK_CONDITIONS.length,
      currentConditionIndex,
      progressState,
    });
    const result = await executeConditionRuns(
      matrix,
      services.length,
      runParameterSchedule,
      condition,
      {
        ...options,
        matrixSeed: seed,
        parallelExecution,
      },
      currentConditionIndex,
      SFLAGA_IMPROVEMENT_BENCHMARK_CONDITIONS.length,
      input.shouldCancel,
      progressHandlers.onAlgorithmStart,
      progressHandlers.onAlgorithmComplete
    );

    conditions.push(buildConditionSummary(condition, result));
  }

  input.onProgress?.({
    phase: 'summary',
    completedUnits: totalUnits,
    totalUnits,
    percent: 100,
    conditionIndex: SFLAGA_IMPROVEMENT_BENCHMARK_CONDITIONS.length,
    totalConditions: SFLAGA_IMPROVEMENT_BENCHMARK_CONDITIONS.length,
    conditionId:
      SFLAGA_IMPROVEMENT_BENCHMARK_CONDITIONS[
        SFLAGA_IMPROVEMENT_BENCHMARK_CONDITIONS.length - 1
      ].id,
    conditionLabel:
      SFLAGA_IMPROVEMENT_BENCHMARK_CONDITIONS[
        SFLAGA_IMPROVEMENT_BENCHMARK_CONDITIONS.length - 1
      ].label,
    improvementLabel:
      SFLAGA_IMPROVEMENT_BENCHMARK_CONDITIONS[
        SFLAGA_IMPROVEMENT_BENCHMARK_CONDITIONS.length - 1
      ].label,
    runNumber: options.numberOfRuns,
    totalRuns: options.numberOfRuns,
    algorithmKey: undefined,
    algorithmName: undefined,
  });

  const conditionIds = conditions.map((condition) => condition.id);

  return {
    generatedAt: new Date().toISOString(),
    experiment: {
      benchmarkCode,
      benchmarkSchemaVersion: SFLAGA_IMPROVEMENT_BENCHMARK_SCHEMA_VERSION,
      taskCount: options.taskCount,
      numberOfRuns: options.numberOfRuns,
      matrixSeed: seed,
      includeSflaGaBaseline: options.includeSflaGaBaseline,
      parallelExecution,
      fitnessMode: DEFAULT_SFLAGA_IMPROVEMENT_BENCHMARK_FITNESS_MODE,
      taskSpecificVariationEnabled: options.taskSpecificVariationEnabled,
      parameterMode,
      parameterSeed,
      serviceCount: services.length,
      conditionIds,
      conditionSignature:
        buildSflagaImprovementBenchmarkConditionSignature(conditionIds),
      sharedParameters,
      runParameters: runParameterSchedule,
    },
    diagnostics: {
      timingMethod: 'performance.now() wall-clock timing',
      resetsCalculatorsPerAlgorithm: true,
      measuredScope: 'Optimization routine only; UI rendering excluded',
      algorithmKeys,
      algorithmNames: algorithmKeys.map((algorithmKey) => RESULT_ALGORITHM_NAMES[algorithmKey]),
      notes: [
        'Fitness and cost calculators are reinitialized before every algorithm run to avoid warmed-cache timing bias.',
        'Benchmark timing disables progress-yield sleeps so execution time reflects search work rather than UI responsiveness scheduling.',
        'Convergence capture records every generation in benchmark mode instead of using throttled UI progress checkpoints.',
        'Execution time is implementation-specific and is most trustworthy when comparing the same task count on the same machine.',
        'Operation counts are instrumentation counters, not hardware profiler measurements.',
        ...(parallelExecution
          ? [
              'Parallel execution runs benchmark algorithms concurrently in isolated Web Workers. This reduces wall-clock completion time, but per-algorithm execution-time comparisons become less strict because the workers contend for CPU.',
            ]
          : []),
        ...(requestedParallelExecution && !parallelExecution
          ? [
              'Parallel execution was requested, but this environment does not expose Web Workers for the benchmark runner, so the benchmark fell back to sequential execution.',
            ]
          : []),
        ...(parameterMode === 'randomized'
          ? [
              'Each run number receives a fresh randomized shared parameter set, and that same set is reused across all algorithms and enhancement conditions for the corresponding run.',
            ]
          : []),
      ],
    },
    conditions,
    crossConditionSflagaRankingByMean: buildCrossConditionRankingByMean(conditions),
    crossConditionSflagaRankingByMedian: buildCrossConditionRankingByMedian(conditions),
    winnerCountsByMean: buildWinnerCounts(
      conditions,
      'mean',
      Object.keys(METRIC_DIRECTIONS) as BenchmarkMetricKey[]
    ),
    winnerCountsByMedian: buildWinnerCounts(
      conditions,
      'median',
      Object.keys(METRIC_DIRECTIONS) as BenchmarkMetricKey[]
    ),
  };
}
